// Customer airtime top-up — Web Portal v2's `isAirtime` screen.
//
// WHAT CHANGED AND WHY
// This was a FIVE-STEP WIZARD: phone → amount → review → pay → done, one panel
// at a time. The design is a single two-column page — the form on the left, a
// Review panel on the right that updates as you type. Those are different
// interactions, not different styling: the wizard hid the total until step
// three, where the design keeps it on screen throughout, which is the whole
// point of a money form.
//
// Layout is the design's: `flex:2 1 440px` form beside `flex:1 1 300px` aside,
// both 22px cards, wrapping to one column when there is no room.
//
// Everything priced comes from the SERVER. The design's Review panel prints a
// fixed "$0.50" service fee; ours asks /airtime/quotes, because the fee is a
// SuperAdmin setting that has already been changed once — it is $1.29 today —
// and a hardcoded number would quietly lie to the sender.

import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, Loader2 } from 'lucide-react';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card } from '../../components/shared/primitives';
import { PaymentPanel } from '../../components/shared/PaymentPanel';
import { CUSTOMER_NAV } from '../_navs';
import { useCustomerNav } from '../../lib/pendingAction';
import {
  airtime, createAirtimeOrder, recipients as recipientsApi,
  type AirtimeOperatorWire, type AirtimeCatalogWire, type AirtimeOrder } from '../../lib/api';

function newIdempotencyKey(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `ik_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }
}

/** Integer minor units -> "$15" / "$4.50". */
function fmtUsd(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2 }).format(cents / 100);
}

// Airtime is Haiti-only, so the country code is never anything but +509.
// Making the sender type it only creates ways to get it wrong.
const HT_DIALLING_CODE = '+509';
const HT_NATIONAL_DIGITS = 8;

function toNationalDigits(raw: string): string {
  let d = raw.replace(/\D/g, '');
  if (d.startsWith('00509')) d = d.slice(5);
  else if (d.startsWith('509') && d.length > HT_NATIONAL_DIGITS) d = d.slice(3);
  return d.slice(0, HT_NATIONAL_DIGITS);
}

/** "37 12 34 56" — the design's grouping. */
function formatNational(d: string): string {
  return [d.slice(0, 2), d.slice(2, 4), d.slice(4, 6), d.slice(6, 8)]
    .filter(Boolean).join(' ');
}

export function AirtimePage() {
  const [national, setNational] = useState('');
  const phone = national ? `${HT_DIALLING_CODE}${national}` : '';
  const phoneComplete = national.length === HT_NATIONAL_DIGITS;

  const [operator, setOperator] = useState<AirtimeOperatorWire | null>(null);
  const [catalog, setCatalog] = useState<AirtimeCatalogWire | null>(null);
  const [amountCents, setAmountCents] = useState<number | null>(null);
  const [order, setOrder] = useState<AirtimeOrder | null>(null);
  const [paid, setPaid] = useState(false);
  const [error, setError] = useState('');

  const nav = useCustomerNav(CUSTOMER_NAV);

  // "Recent" chips — the design shows two. Real recipients, so tapping one
  // fills a number the sender has actually used.
  const recent = useQuery({
    queryKey: ['recipients'],
    queryFn: recipientsApi.list,
    staleTime: 60_000,
  });

  const detectM = useMutation({
    mutationFn: async (p: string) => {
      const det = await airtime.detectOperator(p);
      if (!det.detected || !det.operator) throw new Error('no-operator');
      const cat = await airtime.catalog(det.operator.operator_id);
      return { operator: det.operator, catalog: cat };
    },
    onSuccess: (r) => {
      setOperator(r.operator);
      setCatalog(r.catalog);
      setError('');
      // One click for the common case: the carrier's own most-popular amount.
      if (r.catalog.denomination === 'range' && r.catalog.range?.popular) {
        setAmountCents(r.catalog.range.popular);
      }
    },
    onError: () => setError(
      'We couldn’t confirm the carrier for this number. Check it and try again.',
    ),
  });

  // The REAL price. The design hardcodes a $0.50 fee; the live one is a
  // SuperAdmin setting and is $1.29 today.
  const quote = useQuery({
    queryKey: ['airtime', 'quote', operator?.operator_id, amountCents],
    queryFn: () => airtime.quote(
      operator!.operator_id,
      `${operator!.operator_id}-airtime`,
      amountCents!,
    ),
    enabled: !!operator && amountCents !== null,
  });

  const orderM = useMutation({
    mutationFn: async () => {
      if (!operator || amountCents === null) throw new Error('incomplete');
      const res = await createAirtimeOrder({
        operator_id: operator.operator_id,
        product_ref: `${operator.operator_id}-airtime`,
        recipient_phone: phone,
        amount: amountCents,
      }, newIdempotencyKey());
      return res.order;
    },
    onSuccess: (o) => { setOrder(o); setError(''); },
    onError: () => setError('We could not start that top-up. Please try again.'),
  });

  // Clear the carrier when the number changes, so the Review panel never shows
  // a carrier belonging to a number the sender has since edited.
  useEffect(() => {
    if (!phoneComplete) {
      setOperator(null);
      setCatalog(null);
      setAmountCents(null);
    }
  }, [phoneComplete, national]);

  const range = catalog?.denomination === 'range' ? catalog.range : null;
  const amounts = range
    ? [...new Set([...(range.suggested ?? []), range.popular].filter(
        (v): v is number => typeof v === 'number'))].sort((a, b) => a - b)
    : [];

  const amountError = range && amountCents !== null
    ? amountCents < range.min ? `Minimum is ${fmtUsd(range.min)}.`
      : amountCents > range.max ? `Maximum is ${fmtUsd(range.max)}.`
      : null
    : null;

  const q = quote.data?.quote;
  const canSend = !!operator && amountCents !== null && !amountError && !orderM.isPending;

  return (
    <PortalShell
      nav={nav}
      activeId="airtime"
      title="Send airtime"
      subtitle="Top up any Digicel or Natcom number in Haiti. Credit lands in seconds."
    >
      {/* The design's two-column split: form 2fr, review 1fr, wrapping. */}
      <div className="mt-[22px] flex flex-wrap items-start gap-5">
        <Card padding="p-6" className="min-w-0 flex-[2_1_440px]">
          <p className="text-[12px] font-extrabold tracking-[0.8px] text-[var(--color-muted)]">
            1 · PHONE NUMBER
          </p>

          <div className="mt-2.5 flex flex-wrap gap-2.5">
            <div className="flex min-w-[240px] flex-1 items-center overflow-hidden rounded-[14px] border border-[#C6D2EA]">
              <span className="border-r border-[var(--color-border)] bg-[#F4F6FA] px-3.5 py-[13px] font-mono text-[14px] font-extrabold text-[var(--color-ink)]">
                {HT_DIALLING_CODE}
              </span>
              <input
                value={formatNational(national)}
                onChange={(e) => setNational(toNationalDigits(e.target.value))}
                placeholder="37 12 34 56"
                inputMode="numeric"
                autoComplete="tel-national"
                aria-label="Recipient phone number"
                className="min-w-0 flex-1 px-3.5 py-[13px] font-mono text-[15px] text-[var(--color-ink)] outline-none"
              />
            </div>
            <button
              type="button"
              onClick={() => phoneComplete && detectM.mutate(phone)}
              disabled={!phoneComplete || detectM.isPending}
              className="whitespace-nowrap rounded-[12px] bg-[#1455E8] px-4 py-[11px] text-[13px] font-extrabold text-white hover:bg-[#063B9E] disabled:opacity-50"
            >
              {detectM.isPending ? 'Checking…' : 'Find carrier'}
            </button>
          </div>

          {error && !operator && (
            <p role="alert" className="mt-2.5 text-[13px] text-[var(--color-danger-fg)]">{error}</p>
          )}

          {operator && (
            <div className="mt-3 flex flex-wrap items-center gap-x-2.5 gap-y-1.5 rounded-[12px] border border-[var(--color-success-border)] bg-[var(--color-success-bg)] px-3.5 py-[11px]">
              <Check size={15} strokeWidth={2.8} className="flex-none text-[var(--color-success-fg)]" aria-hidden />
              <span className="whitespace-nowrap text-[13.5px] font-extrabold text-[var(--color-success-deep)]">
                {operator.operator}
              </span>
              <span className="whitespace-nowrap text-[12.5px] text-[var(--color-success-fg)]">
                Carrier confirmed
              </span>
            </div>
          )}

          {(recent.data ?? []).length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-[12px] font-bold text-[var(--color-muted)]">Recent</span>
              {(recent.data ?? []).slice(0, 2).map((r) => {
                const digits = toNationalDigits(r.phone_e164 ?? '');
                if (!digits) return null;
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setNational(digits)}
                    className="whitespace-nowrap rounded-[10px] border border-[var(--color-border)] bg-white px-[11px] py-1.5 text-[12.5px] font-bold text-[var(--color-body)]"
                  >
                    {r.full_name.split(' ')[0]} ·{' '}
                    <span className="font-mono">{formatNational(digits)}</span>
                  </button>
                );
              })}
            </div>
          )}

          <p className="mt-[26px] text-[12px] font-extrabold tracking-[0.8px] text-[var(--color-muted)]">
            2 · AMOUNT
          </p>

          {!operator ? (
            <p className="mt-2.5 text-[13px] text-[var(--color-secondary)]">
              Find the carrier first — the amounts come from their catalog.
            </p>
          ) : (
            <>
              <div
                className="mt-2.5 grid gap-2.5"
                style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(110px,1fr))' }}
              >
                {amounts.map((cents) => {
                  const on = amountCents === cents;
                  return (
                    <button
                      key={cents}
                      type="button"
                      onClick={() => setAmountCents(cents)}
                      aria-pressed={on}
                      className={`rounded-[14px] py-4 text-[18px] font-extrabold tabular-nums ${
                        on
                          ? 'border border-[#1455E8] bg-[#1455E8] text-white'
                          : 'border border-[var(--color-border)] bg-white text-[var(--color-ink)]'
                      }`}
                    >
                      {fmtUsd(cents)}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2.5 text-[12px] text-[var(--color-muted)]">
                Amounts come from the carrier’s live catalog.
              </p>
              {amountError && (
                <p role="alert" className="mt-2 text-[13px] text-[var(--color-danger-fg)]">
                  {amountError}
                </p>
              )}
            </>
          )}
        </Card>

        {/* Review — the design's aside. It stays on screen so the total is
            never hidden behind a step. */}
        <Card
          padding="p-[22px]"
          className="flex-[1_1_300px] shadow-[0_18px_40px_rgba(11,18,32,0.08)]"
        >
          {order && paid ? (
            <div className="py-2.5 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-[28px] bg-[var(--color-success-bg)]">
                <Check size={26} strokeWidth={2.8} className="text-[var(--color-success-fg)]" aria-hidden />
              </span>
              <p className="mt-3 text-[17px] font-extrabold text-[var(--color-ink)]">Top-up sent</p>
              <p className="mt-1 text-[13px] text-[var(--color-secondary)]">
                {operator?.operator} · {HT_DIALLING_CODE} {formatNational(national)}
              </p>
              <button
                type="button"
                onClick={() => {
                  setNational(''); setOperator(null); setCatalog(null);
                  setAmountCents(null); setOrder(null); setPaid(false);
                }}
                className="mt-4 w-full rounded-[14px] border border-[var(--color-border)] bg-white py-3 text-[14px] font-extrabold text-[#1455E8]"
              >
                Send another
              </button>
            </div>
          ) : order ? (
            <>
              <p className="text-[17px] font-extrabold text-[var(--color-ink)]">Pay</p>
              <div className="mt-3.5">
                <PaymentPanel
                  payableType="airtime"
                  payableId={order.id}
                  onPaid={() => setPaid(true)}
                />
              </div>
            </>
          ) : (
            <>
              <p className="text-[17px] font-extrabold text-[var(--color-ink)]">Review</p>

              <dl className="mt-3.5 flex flex-col gap-2.5">
                <Line
                  label="Phone"
                  mono
                  value={national ? `${HT_DIALLING_CODE} ${formatNational(national)}` : '—'}
                />
                <Line label="Carrier" bold value={operator?.operator ?? 'Not detected yet'} />
                <Line label="Top-up" bold value={amountCents !== null ? fmtUsd(amountCents) : '—'} />
                <Line
                  label="Service fee"
                  bold
                  value={q ? fmtUsd(q.fee) : amountCents !== null ? '…' : '—'}
                />
              </dl>

              <div className="mt-1 flex items-baseline justify-between border-t border-dashed border-[#DCE4F6] pt-3">
                <span className="text-[14px] font-extrabold text-[var(--color-ink)]">Total</span>
                <span className="text-[24px] font-extrabold tabular-nums tracking-[-0.6px] text-[var(--color-ink)]">
                  {q ? fmtUsd(q.total) : '—'}
                </span>
              </div>

              {error && operator && (
                <p role="alert" className="mt-2 text-[12.5px] text-[var(--color-danger-fg)]">
                  {error}
                </p>
              )}

              <button
                type="button"
                onClick={() => orderM.mutate()}
                disabled={!canSend || !q}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-[14px] bg-[#1455E8] py-3.5 text-[14px] font-extrabold text-white hover:bg-[#063B9E] disabled:bg-[#B9C3D9]"
              >
                {orderM.isPending && <Loader2 size={15} className="animate-spin" aria-hidden />}
                {q ? `Send ${fmtUsd(q.total)}` : 'Send'}
              </button>

              <p className="mt-2.5 text-center text-[12px] text-[var(--color-muted)]">
                You approve the card on the next step.
              </p>
            </>
          )}
        </Card>
      </div>
    </PortalShell>
  );
}

function Line({ label, value, mono, bold }: {
  label: string; value: string; mono?: boolean; bold?: boolean;
}) {
  return (
    <div className="flex justify-between text-[13px]">
      <dt className="text-[var(--color-secondary)]">{label}</dt>
      <dd className={`text-[var(--color-ink)] ${mono ? 'font-mono' : ''} ${bold ? 'font-bold' : ''}`}>
        {value}
      </dd>
    </div>
  );
}
